import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, Radio, TrendingUp } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchConsoleChannels, formatFcfa, type ConsoleChannels } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA channel mix + WhatsApp console (Epic 6) — order volume and revenue
// split across the sales channels (app, WhatsApp text/voice, field agent, call
// center) plus the count of currently open WhatsApp session windows. All reads
// are RLS-scoped through the `djayma` schema; a build without migrations
// 131-140 or without the schema exposed simply returns nothing.
// ---------------------------------------------------------------------------

const CHANNEL_LABELS: Record<string, string> = {
  app: "App",
  whatsapp_text: "WhatsApp (texte)",
  whatsapp_voice: "WhatsApp (vocal)",
  agent: "Agent terrain",
  call_center: "Centre d'appel",
};

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function ChannelMix() {
  const q = useQuery({ queryKey: ["djayma", "console-channels"], queryFn: fetchConsoleChannels, retry: false });

  const data: ConsoleChannels = q.data ?? { channels: [], whatsapp_open: 0 };

  const totalOrders = data.channels.reduce((acc, c) => acc + Number(c.orders ?? 0), 0);
  const totalRevenue = data.channels.reduce((acc, c) => acc + Number(c.revenue_xof ?? 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Radio className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Canaux &amp; WhatsApp</h1>
            <p className="text-sm text-stone-500">
              Répartition des commandes par canal et sessions WhatsApp ouvertes
            </p>
          </div>
        </div>

        {q.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Console des canaux non joignable. Vérifiez les migrations 131-140 et l&apos;exposition du schéma
            <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Kpi
            icon={<TrendingUp className="h-5 w-5" />}
            tone="bg-stone-100 text-stone-700"
            label="Total commandes"
            value={totalOrders}
          />
          <Kpi
            icon={<TrendingUp className="h-5 w-5" />}
            tone="bg-amber-100 text-amber-700"
            label="Chiffre d'affaires total"
            value={formatFcfa(totalRevenue)}
          />
          <Kpi
            icon={<MessageCircle className="h-5 w-5" />}
            tone="bg-emerald-100 text-emerald-700"
            label="Sessions WhatsApp ouvertes"
            value={data.whatsapp_open}
          />
        </div>

        {/* Channel mix table */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Commandes par canal</h2>
          {data.channels.length === 0 ? (
            <p className="text-sm text-stone-400">Aucune commande par canal.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Canal</th>
                    <th className="py-2 pr-4 font-medium text-right">Commandes</th>
                    <th className="py-2 pr-4 font-medium text-right">Chiffre d'affaires</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {data.channels.map((c) => (
                    <tr key={c.channel}>
                      <td className="py-2 pr-4 text-stone-700">{CHANNEL_LABELS[c.channel] ?? c.channel}</td>
                      <td className="py-2 pr-4 text-right text-stone-500">{c.orders}</td>
                      <td className="py-2 pr-4 text-right font-medium text-stone-800">{formatFcfa(c.revenue_xof)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
